/**
 * GrantDetail — `/grants/:id`.
 *
 * CRITICAL layout constraint (G1 dispatch, non-negotiable): the
 * EligibilitySnapshot renders ABOVE THE FOLD on a 1366×768 viewport.
 * The header + eligibility card together fit into < 640px of vertical
 * space so users see the tri-state badge + rule-by-rule breakdown
 * without any scrolling.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  HelpCircle,
  Wrench,
  XCircle,
  Target,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import GrantShell from "@/components/grants/GrantShell";
import EligibilityBadge from "@/components/grants/EligibilityBadge";
import SaveGrantButton from "@/components/grants/SaveGrantButton";
import MatchSection from "@/components/grants/MatchSection";
import ReadinessSection from "@/components/documents/ReadinessSection";
import { Card } from "@/design-system/components/Card";
import { Heading } from "@/design-system/components/Heading";
import { toast } from "sonner";

const STATUS_ICON = {
  met: { Icon: CheckCircle2, color: "var(--ds-color-success)" },
  not_met: { Icon: XCircle, color: "var(--ds-color-danger)" },
  unknown: { Icon: HelpCircle, color: "var(--ds-color-warning)" },
};

/**
 * Frontend Discipline 1 (Patch C carry-through): a failing rule's copy
 * must reflect the `correctable` classification.
 *
 * `yes`         → phrased as an action the user can take.
 * `time_based`  → phrased as a natural progression over time.
 * `no`          → stated plainly as a structural fact (no false hope).
 * `unknown`     → phrased as a profile-completion prompt.
 */
function reasonCopy(rule) {
  if (rule.met === "met") return rule.description || "Meets this requirement.";
  if (rule.met === "unknown") {
    return rule.note || "Add this information to your grant profile to complete the check.";
  }
  // not_met
  if (rule.correctable === "yes") {
    return rule.note || "Take a corrective action to meet this requirement.";
  }
  if (rule.correctable === "time_based") {
    return rule.note || "This requirement will be met naturally as your business matures.";
  }
  return rule.note || "This grant is limited in a way that cannot be corrected on your side.";
}

function correctablePill(rule) {
  if (rule.met === "met") return null;
  const meta = {
    yes: { label: "Actionable", color: "var(--ds-color-success)", Icon: Wrench },
    time_based: { label: "Time-based", color: "var(--ds-color-info)", Icon: Clock },
    no: { label: "Structural", color: "var(--ds-color-ink-muted)", Icon: XCircle },
  }[rule.correctable];
  if (!meta) return null;
  const Icon = meta.Icon;
  return (
    <span
      data-testid={`rule-correctable-pill-${rule.correctable}-${rule.key}`}
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold"
      style={{ background: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}44` }}
    >
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function RuleRow({ rule }) {
  const s = STATUS_ICON[rule.met] || STATUS_ICON.unknown;
  const Icon = s.Icon;
  return (
    <li
      data-testid={`rule-row-${rule.key}`}
      className="flex items-start gap-3 py-2.5"
    >
      <Icon className="mt-0.5 w-4 h-4 flex-shrink-0" style={{ color: s.color }} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-[var(--ds-color-ink)] font-medium">
            {rule.description || rule.key.replace(/_/g, " ")}
          </p>
          {correctablePill(rule)}
        </div>
        <p className="mt-0.5 text-xs text-[var(--ds-color-ink-muted)] leading-relaxed">
          {reasonCopy(rule)}
        </p>
      </div>
    </li>
  );
}

function formatAward(min, max) {
  if (min == null && max == null) return "Amount varies by cycle";
  if (min && max && min === max) return `$${Number(min).toLocaleString()}`;
  if (min && max) return `$${Number(min).toLocaleString()} – $${Number(max).toLocaleString()}`;
  if (max) return `Up to $${Number(max).toLocaleString()}`;
  return `From $${Number(min).toLocaleString()}`;
}

export default function GrantDetail() {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [missionSet, setMissionSet] = useState(true); // optimistic — hide hint until we know

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setState({ loading: true, data: null, error: null });
      try {
        const res = await api.get(`/grants/${id}`);
        if (!cancelled) setState({ loading: false, data: res.data, error: null });
      } catch (err) {
        if (!cancelled) setState({ loading: false, data: null, error: formatApiError(err) });
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  // Best-effort read of grant_profile.mission_statement — feeds the
  // MatchSection helper hint. 404 (no profile row yet) also counts
  // as "unset".
  useEffect(() => {
    let cancelled = false;
    api
      .get("/grant-profile")
      .then((res) => {
        if (!cancelled) {
          const m = res?.data?.mission_statement;
          setMissionSet(typeof m === "string" && m.trim().length > 0);
        }
      })
      .catch(() => {
        if (!cancelled) setMissionSet(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.loading) {
    return (
      <GrantShell testId="grant-detail-root">
        <div className="mt-10 text-sm text-[var(--ds-color-ink-muted)]">Loading grant…</div>
      </GrantShell>
    );
  }
  if (state.error) {
    return (
      <GrantShell testId="grant-detail-root">
        <Card className="mt-8 rounded-[var(--ds-radius-card)]">
          <div className="p-6">
            <p
              className="text-[14px] font-semibold"
              style={{ color: "var(--ds-color-ink)" }}
            >
              This grant is not currently available.
            </p>
            <p
              className="mt-1 text-[13px]"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {state.error}
            </p>
            <Link
              to="/grants"
              data-testid="grant-detail-back-to-finder"
              className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold hover:underline"
              style={{ color: "var(--ds-color-accent)" }}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Grant Finder
            </Link>
          </div>
        </Card>
      </GrantShell>
    );
  }

  const { grant, eligibility_snapshot, is_saved } = state.data;

  return (
    <GrantShell testId="grant-detail-root">
      <Link
        to="/grants"
        data-testid="grant-detail-back"
        className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Grant Finder
      </Link>

      {/* Header — compact so the snapshot below stays above the fold. */}
      <header className="mt-2 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="min-w-0">
          <p
            className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            <Building2 className="w-3 h-3" aria-hidden="true" /> {grant.agency}
          </p>
          <Heading
            level={1}
            size="2xl"
            weight="semibold"
            className="mt-1 leading-tight"
            data-testid="grant-detail-title"
          >
            {grant.title}
          </Heading>
        </div>
        <div className="flex items-center gap-3">
          <SaveGrantButton
            grantId={grant.id}
            initialSaved={is_saved}
            testIdPrefix="grant-detail-save"
            size="lg"
          />
        </div>
      </header>

      {/* ELIGIBILITY SNAPSHOT — must render above the fold at 1366×768. */}
      <Card
        data-testid="grant-detail-eligibility-snapshot"
        className="mt-3 rounded-[var(--ds-radius-card)]"
      >
        <div className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <p
              className="text-[11px] uppercase tracking-[0.16em] font-semibold"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Eligibility Snapshot
            </p>
            <EligibilityBadge status={eligibility_snapshot.status} size="lg" />
          </div>
          {eligibility_snapshot.summary_reason && (
            <p
              data-testid="grant-detail-eligibility-summary-reason"
              className="mt-3 text-[13px]"
              style={{ color: "var(--ds-color-ink)" }}
            >
              {eligibility_snapshot.summary_reason}
            </p>
          )}
          {(eligibility_snapshot.rules_evaluated || []).length === 0 ? (
            <p
              className="mt-3 text-[13px]"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              No specific eligibility rules on file — subject to the grant sponsor's final review.
            </p>
          ) : (
            <ul
              className="mt-3 divide-y"
              style={{ borderColor: "var(--ds-color-border)" }}
            >
              {eligibility_snapshot.rules_evaluated.map((rule) => (
                <RuleRow key={rule.key + rule.description} rule={rule} />
              ))}
            </ul>
          )}
        </div>
      </Card>

      {/* Phase G3B CP2 — Application Readiness section. Rendered
          between the Eligibility Snapshot (ATF-preserved) and the AI
          Match block. Fetches its own `/document-readiness` payload;
          shows null-percentage empty state for grants that don't list
          required documents. */}
      <ReadinessSection grantId={grant.id} />

      {/* Phase G2A CP3 — AI Match Analysis section. Rendered BELOW the
          Eligibility Snapshot so the snapshot's above-the-fold budget
          at 1366×768 is preserved. `MatchSection` internally handles
          the not_eligible / idle / cached-result / capped states so
          this file stays flat. */}
      <MatchSection
        grantId={grant.id}
        eligibilityStatus={eligibility_snapshot.status}
        missionStatementSet={missionSet}
      />

      {/* Below-the-fold: award, deadline, difficulty, docs, apply link. */}
      <section className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <MetaCard label="Award range" value={formatAward(grant.award_amount_min, grant.award_amount_max)} />
        <MetaCard label="Deadline" value={humanDeadline(grant)} />
        <MetaCard label="Difficulty" value={(grant.difficulty || "").toUpperCase()} />
      </section>

      <Card className="mt-6 rounded-[var(--ds-radius-card)]">
        <div className="p-5">
          <p
            className="text-[11px] uppercase tracking-[0.16em] font-semibold"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            Program summary
          </p>
          <p
            className="mt-2 text-[13px] leading-snug whitespace-pre-line"
            style={{ color: "var(--ds-color-ink)" }}
          >
            {grant.program_summary}
          </p>
        </div>
      </Card>

      {(grant.required_documents || []).length > 0 && (
        <Card className="mt-4 rounded-[var(--ds-radius-card)]">
          <div className="p-5">
            <p
              className="text-[11px] uppercase tracking-[0.16em] font-semibold"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Required documents
            </p>
            <ul
              className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-4 text-[13px]"
              style={{ color: "var(--ds-color-ink)" }}
            >
              {grant.required_documents.map((d) => (
                <li key={d} className="flex items-center gap-2">
                  <Target
                    className="w-3.5 h-3.5"
                    style={{ color: "var(--ds-color-accent)" }}
                    aria-hidden="true"
                  />
                  {d.replace(/_/g, " ")}
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}

      <section className="mt-6 flex flex-wrap items-center gap-4">
        {grant.official_application_url && (
          <a
            href={grant.official_application_url}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="grant-detail-official-apply"
            className="inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold transition-colors"
            style={{
              background: "var(--ds-color-accent)",
              color: "var(--ds-color-accent-ink)",
              transitionDuration: "var(--ds-duration-fast)",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--ds-color-accent-strong)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "var(--ds-color-accent)")
            }
          >
            Visit sponsor page <ArrowUpRight className="w-4 h-4" />
          </a>
        )}
        {grant.source_verified_at && (
          <span
            className="text-[11px]"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            Source verified: {new Date(grant.source_verified_at).toISOString().slice(0, 10)}
          </span>
        )}
      </section>
    </GrantShell>
  );
}

function MetaCard({ label, value }) {
  return (
    <Card className="rounded-[var(--ds-radius-card)]">
      <div className="p-4">
        <p
          className="text-[11px] uppercase tracking-[0.16em] font-semibold"
          style={{ color: "var(--ds-color-ink-subtle)" }}
        >
          {label}
        </p>
        <p
          className="mt-1 text-[16px] font-semibold"
          style={{ color: "var(--ds-color-ink)" }}
        >
          {value}
        </p>
      </div>
    </Card>
  );
}

function humanDeadline(grant) {
  if (grant.deadline_type === "rolling") return "Rolling";
  if (!grant.deadline) return "Next cycle TBD";
  try {
    return new Date(grant.deadline).toISOString().slice(0, 10);
  } catch {
    return "Next cycle TBD";
  }
}
